import moment from 'moment-timezone';
import type { TFunction } from 'i18next';

import type { DateRangeValue } from 'app/components/DateRangePicker/types';

const ONE_MINUTE_SEC = 60;
const ONE_SECOND = 1;

function getTimestamp(value: string | null | undefined): number | null;
function getTimestamp(value: string | null | undefined, defaultValue: number): number;
function getTimestamp(value: string | null | undefined, defaultValue?: number): number | null {
  if (!value) {
    return defaultValue ?? null;
  }
  const timestamp = new Date(value).getTime();

  if (isNaN(timestamp)) {
    return defaultValue ?? null;
  }

  return timestamp;
}

/**
 * @param timestamp match timestamp in seconds
 */
function formatGameTimestamp(timestamp: number): string {
  if (timestamp === 0) {
    return '0:00';
  }
  const absTimeout = Math.abs(timestamp);
  const prefix = timestamp >= 0 ? '' : '-';

  const nbMinutes = Math.floor(absTimeout / ONE_MINUTE_SEC);
  const nbSeconds = Math.round((absTimeout - nbMinutes * ONE_MINUTE_SEC) / ONE_SECOND);
  const seconds = nbSeconds > 9 ? nbSeconds : `0${nbSeconds}`;

  return `${prefix}${nbMinutes}:${seconds}`;
}

function decomposeDuration(duration: number): [number, number] {
  if (duration < 0) {
    console.warn(`TimestampPlayer: duration "${duration}" is negative`);
    return [0, 0];
  }

  const minutes = Math.floor(duration / 60);
  const seconds = duration - minutes * 60;

  return [minutes, seconds];
}

function getTodayPeriod(): DateRangeValue {
  const today = new Date();
  today.setHours(0);
  today.setMinutes(0);
  today.setSeconds(0);
  today.setMilliseconds(0);
  const start = today.getTime();
  today.setHours(23);
  today.setMinutes(59);
  today.setSeconds(59);
  today.setMilliseconds(999);
  const end = today.getTime();

  return {
    start,
    end
  };
}

function getElapsedDuration(
  start: number | null,
  end: number | null,
  format: { days?: boolean; hours?: boolean; minutes?: boolean; seconds?: boolean }
): string | null {
  if (!start || !end) {
    return null;
  }

  const units = {
    days: 24 * 60 * 60 * 1000,
    hours: 60 * 60 * 1000,
    minutes: 60 * 1000,
    seconds: 1000
  };

  const ellapsedMs = Math.abs(end - start);

  const processedTime = (Object.entries(units) as [keyof typeof units, number][]).reduce<{
    value: string;
    ellapsedMsLeft: number;
  }>(
    (acc, [unitType, unitValue]) => {
      if (format[unitType] || (unitType === 'seconds' && acc.value === '')) {
        const ellapsedUnitValue = Math.floor(acc.ellapsedMsLeft / unitValue);
        if (ellapsedUnitValue > 0 || (unitType === 'seconds' && acc.value === '')) {
          const ellapsedMsLeft = acc.ellapsedMsLeft % unitValue;
          return { value: `${acc.value} ${ellapsedUnitValue}${unitType.at(0)}`, ellapsedMsLeft };
        }
      }

      return acc;
    },
    { value: '', ellapsedMsLeft: ellapsedMs }
  );

  return processedTime.value.trim();
}

const getTimezone = (): string => moment.tz.guess() || 'Europe/Paris';

const toISOStringForBackend = (date: Date): string => date.toISOString().replace('Z', '000Z');

const formatHoursDurationForDoot = (hours: number): string => {
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  const daysString = days > 0 ? `${days}D` : '';
  const hoursString = remainingHours > 0 ? `${remainingHours}H` : '';

  return [daysString, hoursString].filter(Boolean).join(' ');
};

const formatHoursDurationForDisplay = (hours: number | undefined, t: TFunction): string => {
  if (hours === undefined) {
    return '';
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  const daysString = days > 0 ? `${days} ${t('common-day', { count: days })}` : '';
  const hoursString =
    remainingHours > 0 ? `${remainingHours} ${t('common-hour', { count: hours })}` : '';

  return [daysString, hoursString].filter(Boolean).join(' ');
};

export {
  getTimestamp,
  formatGameTimestamp,
  decomposeDuration,
  getTodayPeriod,
  getElapsedDuration,
  getTimezone,
  toISOStringForBackend,
  formatHoursDurationForDoot,
  formatHoursDurationForDisplay
};
